import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import TextInput from '~components/FormFields/v2/TextInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { KMLFile } from '~hooks/useKMLFile/models';
import { useKMLFile } from '~hooks/useKMLFile/useKML';

const mapFileEditSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .min(1, t('form_validation_errors.required_simple'))
    .required(t('form_validation_errors.required_simple')),
});

type MapFileEditFormValues = yup.InferType<typeof mapFileEditSchema>;

interface MapFileEditDialogProps {
  companyId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (data: KMLFile) => Promise<void>;
  defaultKMLFile: KMLFile | null;
}

export const MapFileEditDialog = ({
  companyId,
  open,
  onClose,
  onSuccess,
  defaultKMLFile,
}: MapFileEditDialogProps) => {
  const { updateKMLFile } = useKMLFile();

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = useForm<MapFileEditFormValues>({
    resolver: yupResolver(mapFileEditSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { name: defaultKMLFile?.name ?? '' },
  });

  useEffect(() => {
    reset({ name: defaultKMLFile?.name ?? '' });
  }, [defaultKMLFile, reset]);

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async ({ name }: MapFileEditFormValues) => {
      if (!defaultKMLFile) {
        return;
      }

      const updatedFile = await updateKMLFile(companyId, defaultKMLFile.id, name);

      if (updatedFile) {
        await onSuccess(updatedFile);
      }

      handleClose();
    },
    [companyId, defaultKMLFile, handleClose, onSuccess, updateKMLFile],
  );

  return (
    <Dialog isOpen={open} onClose={handleClose} maxWidth="sm">
      <DialogHeader onClose={handleClose}>
        {t('administration.map_files.edit_file_title')}
      </DialogHeader>

      <DialogContent>
        <TextInput
          control={control}
          isRequired
          label={t('form_fields.name')}
          name="name"
        />
      </DialogContent>

      <DialogActions>
        <SmallButton color="secondary" onClick={handleClose} variant="outlined">
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          color="primary"
          loading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
          variant="contained"
        >
          {t('actions.save')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};
